import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

export function PeriodClosePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [year, setYear] = useState(new Date().getFullYear());
  const [msg, setMsg] = useState(null);

  const periods = useQuery({
    queryKey: ['finance', 'periods', year],
    queryFn: () => financeApi.listPeriods(year).then((r) => r.items),
  });
  const data = periods.data || { periods: [], yearClosed: false };
  const closedCount = data.periods.filter((p) => p.closed).length;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance', 'periods', year] });

  const close = useMutation({ mutationFn: (p) => financeApi.closePeriod(p), onSuccess: () => { setMsg(null); invalidate(); }, onError: (e) => setMsg(e?.message) });
  const reopen = useMutation({ mutationFn: (p) => financeApi.reopenPeriod(p), onSuccess: () => { setMsg(null); invalidate(); }, onError: (e) => setMsg(e?.message) });
  const yearEnd = useMutation({
    mutationFn: () => financeApi.yearEndClose(year),
    onSuccess: (d) => { setMsg(t('period.yearEnd.done', { net: formatCurrency(d.netIncome) })); invalidate(); },
    onError: (e) => setMsg(e?.message || t('common.loadError')),
  });

  const busy = close.isPending || reopen.isPending || yearEnd.isPending;

  const columns = [
    { key: 'period', label: t('period.col.period'), render: (r) => <span className="mono">{r.period}</span> },
    {
      key: 'status',
      label: t('period.col.status'),
      render: (r) => (r.closed
        ? <Badge variant="neutral">{t('period.status.closed')}</Badge>
        : <Badge variant="success">{t('period.status.open')}</Badge>),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) => (r.closed
        ? <Button size="sm" variant="ghost" disabled={busy} onClick={() => reopen.mutate(r.period)}>{t('period.action.reopen')}</Button>
        : <Button size="sm" variant="ghost" disabled={busy} onClick={() => close.mutate(r.period)}>{t('period.action.close')}</Button>),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('period.title')}</h1>
          <p className="muted">{t('period.subtitle')}</p>
        </div>
        <div className="page-actions">
          <label className="field" style={{ maxWidth: 120 }}>
            <span className="field-label">{t('period.year')}</span>
            <input className="input" type="number" min="2000" max="2100" value={year}
              onChange={(e) => { setMsg(null); setYear(Number(e.target.value)); }} />
          </label>
          <Button variant="primary" disabled={busy || data.yearClosed}
            onClick={() => { if (window.confirm(t('period.yearEnd.confirm', { year: String(year) }))) yearEnd.mutate(); }}>
            {data.yearClosed ? t('period.yearClosed') : t('period.yearEnd.action')}
          </Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('period.metric.year')} value={String(year)} />
        <MetricCard label={t('period.metric.closed')} value={`${closedCount} / 12`} />
        <MetricCard label={t('period.metric.state')} value={data.yearClosed ? t('period.status.closed') : t('period.status.open')}
          trendVariant={data.yearClosed ? 'neutral' : 'success'} />
      </div>

      {msg && <div className="alert info">{msg}</div>}

      <Table columns={columns} rows={data.periods} isLoading={periods.isLoading} emptyMessage={t('period.empty')} />
    </section>
  );
}
